// Fact extractors: how a scanned game's engine facts are read, one concern
// per file under `facts/`. Each extractor reads the game through a shared
// `ScannedGame` (the scan's scores from the reader's side, the stage ranges,
// the review's judgements) and writes its own fields onto the game's facts.
// The fields are declared on `GameFacts` in the model (`@chess/api-model`,
// `facts.ts`, under the extractor's file name), so every reader sees their
// type: a new kind of fact is a new file here, its fields there, and one line
// in `facts/index.ts`.
import { DEFAULT_MISTAKE_RULES, type Color, type MistakeRules, type Move, type PositionAnalysis, type Scan, type Phase, type ReviewPhases } from '@chess/api-model';
import { GameScan, phaseRange, readerPawns, scoreForReader, sideToMove } from '@chess/api-review';
import { type GameFacts } from '@chess/api-model';

/** Scores are capped at this many pawns either way, so one mate does not swamp an average. */
export const SCORE_CAP = 10;
export const capPawns = (pawns: number) => Math.max(-SCORE_CAP, Math.min(SCORE_CAP, pawns));

/** Pawns up, from the reader's side, that count as a winning position, under the default rules (the user's are `ScannedGame.rules`). */
export const WINNING_PAWNS = DEFAULT_MISTAKE_RULES.winningPawns;
/** Below this many pawns up after the move, the win is gone, under the default rules. */
export const HELD_PAWNS = DEFAULT_MISTAKE_RULES.heldPawns;

/** A scanned game as the extractors read it: one instance per game, shared by all of them. */
export class ScannedGame {
  readonly moves: Move[];
  readonly phases: ReviewPhases;
  readonly scan: Scan;
  readonly reader: Color;
  /** The review's judgement of each move, under `rules`. */
  readonly judge: GameScan;
  /** The blunder and miss rules every extractor follows: the user's, or the defaults. */
  readonly rules: MistakeRules;
  /** Seconds left on the mover's clock after each ply, by ply - 1 (`../clocks.ts`); empty when the PGN has no clocks. */
  readonly clocks: ReadonlyArray<number | undefined>;
  private readonly positions = new Map<number, PositionAnalysis>();

  constructor(moves: Move[], phases: ReviewPhases, scan: Scan, reader: Color, rules: MistakeRules = DEFAULT_MISTAKE_RULES, clocks: ReadonlyArray<number | undefined> = []) {
    this.moves = moves;
    this.clocks = clocks;
    this.phases = phases;
    this.scan = scan;
    this.reader = reader;
    this.rules = rules;
    this.judge = new GameScan(scan, rules);
    for (const p of scan.positions) this.positions.set(p.ply, p);
  }

  /** The scan's entry for the position after `ply` plies. */
  at(ply: number): PositionAnalysis | undefined {
    return this.positions.get(ply);
  }

  /** The reader's score in pawns after `ply` plies, capped; undefined where the scan has none. */
  pawnsAt(ply: number): number | undefined {
    const p = this.at(ply);
    return p ? capPawns(readerPawns(scoreForReader(p.score, sideToMove(p.fen), this.reader))) : undefined;
  }

  /** A stage's plies: the reader's moves in it are those after `fromPly` up to `toPly`. */
  range(phase: Phase): { fromPly: number; toPly: number } {
    return phaseRange(this.phases, this.moves.length, phase, this.reader);
  }

  /** Whether the move at this ply (1-based) is the reader's. */
  isReaders(ply: number): boolean {
    return (ply % 2 === 1 ? 'white' : 'black') === this.reader;
  }

  /** Seconds the mover had left after the move at this ply (1-based); undefined without a clock. */
  clockAfter(ply: number): number | undefined {
    return this.clocks[ply - 1];
  }

  /** Whether the move at this ply took a winning position to one under the held line (the rules' `winningPawns` and `heldPawns`). */
  threw(ply: number): boolean {
    const before = this.pawnsAt(ply - 1);
    const after = this.pawnsAt(ply);
    return before !== undefined && after !== undefined && before >= this.rules.winningPawns && after < this.rules.heldPawns;
  }
}

/** One kind of engine fact: reads the scanned game, writes its fields onto the facts. */
export interface FactExtractor {
  /** For warnings; unique among the extractors. */
  readonly id: string;
  extract(game: ScannedGame, facts: GameFacts): void;
}
